import {
  DEV_ADMIN_EMAIL,
  DEV_ADMIN_PASSWORD,
  DEV_FRESH_INVESTOR_EMAIL,
  DEV_INVESTOR_EMAIL,
  DEV_KYC_EXPIRED_EMAIL,
  DEV_KYC_REJECTED_EMAIL,
  DEV_LIMIT_REACHED_EMAIL,
  DEV_ONBOARDING_INVESTOR_EMAIL,
  DEV_READY_TO_VERIFY_EMAIL,
  DEV_REFLECTION_INVESTOR_EMAIL,
  DEV_FIU_HOLD_INVESTOR_EMAIL,
  DEV_VERIFIED_INVESTOR_EMAIL,
} from '@arya/shared/dev-accounts';
import type { RegulatoryRegime } from '@arya/shared/regulatory';

import type { DemoPersona } from './demo-persona';

const globalPersonas = (basePath: string): readonly DemoPersona[] => [
  {
    email: DEV_INVESTOR_EMAIL,
    label: 'Returning investor',
    caption: 'Verified, four holdings, wallet balance',
    initials: 'RI',
    home: `${basePath}/account/portfolio`,
  },
  {
    email: DEV_REFLECTION_INVESTOR_EMAIL,
    label: 'Reservation in reflection period',
    caption: 'Verified, one reservation still revocable',
    initials: 'RP',
    home: `${basePath}/account/portfolio`,
  },
  {
    email: DEV_LIMIT_REACHED_EMAIL,
    label: 'Investment limit reached',
    caption: 'Non-sophisticated, EUR 1,000 recommended limit',
    initials: 'IL',
    home: `${basePath}/market/projects/2?invest=2500`,
  },
  {
    email: DEV_VERIFIED_INVESTOR_EMAIL,
    label: 'Verified, not invested yet',
    caption: 'Identity done, empty portfolio, authenticator + SMS + backup codes',
    initials: 'VN',
    home: `${basePath}/market`,
  },
  {
    email: DEV_ONBOARDING_INVESTOR_EMAIL,
    label: 'Onboarding in progress',
    caption: 'Identity in manual review',
    initials: 'OP',
    home: `${basePath}/onboarding`,
  },
  {
    email: DEV_READY_TO_VERIFY_EMAIL,
    label: 'Ready to verify',
    caption: 'Details and questionnaire done, identity not started',
    initials: 'RV',
    home: `${basePath}/onboarding`,
  },
  {
    email: DEV_KYC_REJECTED_EMAIL,
    label: 'KYC rejected',
    caption: 'Identity check failed, resubmission needed',
    initials: 'KR',
    home: `${basePath}/onboarding`,
  },
  {
    email: DEV_KYC_EXPIRED_EMAIL,
    label: 'KYC expired',
    caption: 'Identity verification lapsed, renewal needed',
    initials: 'KE',
    home: `${basePath}/onboarding`,
  },
  {
    email: DEV_FRESH_INVESTOR_EMAIL,
    label: 'Fresh registration',
    caption: 'Risk acknowledgement pending',
    initials: 'FR',
    home: basePath,
  },
  {
    email: DEV_FIU_HOLD_INVESTOR_EMAIL,
    label: 'Account on FIU hold',
    caption: 'Verified, blocked by an active FIU hold',
    initials: 'FH',
    home: `${basePath}/account/portfolio`,
  },
  {
    email: DEV_ADMIN_EMAIL,
    label: 'Operator (admin)',
    caption: 'Compliance console, authenticator',
    initials: 'OP',
    home: '/admin',
    password: DEV_ADMIN_PASSWORD,
  },
];

type PersonaCopy = Partial<Pick<DemoPersona, 'label' | 'caption' | 'home'>>;

const indonesianCopy = (basePath: string): Readonly<Record<string, PersonaCopy>> => ({
  [DEV_INVESTOR_EMAIL]: { caption: 'Putu Wijaya · verified, holdings in Bali villas, IDR wallet' },
  [DEV_REFLECTION_INVESTOR_EMAIL]: {
    label: 'Reservation in cooling-off period',
    caption: 'Verified, one reservation still cancellable within 2 business days',
  },
  [DEV_LIMIT_REACHED_EMAIL]: {
    label: 'Annual investment limit',
    caption: 'Self-assessed income, 5 % of it per rolling 12 months',
    home: `${basePath}/market/projects/2?invest=5000000`,
  },
  [DEV_FRESH_INVESTOR_EMAIL]: { caption: 'Investor profile, questionnaire and OJK risk notice pending' },
});

export const demoPersonas = (basePath: string, regime: RegulatoryRegime): readonly DemoPersona[] => {
  const copy = regime === 'ojk-sandbox' ? indonesianCopy(basePath) : {};
  return globalPersonas(basePath).map((persona) => ({ ...persona, ...copy[persona.email] }));
};
